import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { SitePartSupplierApi } from "src/api/generated/erp/parts/sitePart/api/sitePartSupplierApi.ts";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { coloredBooleanColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { SupplierPartView } from "src/api/generated/erp/db/types/tables/supplierPartView.ts";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { faCheck, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { OpenObjectChangeLogButton } from "src/components/views/changeLogging/OpenObjectChangeLogButton.tsx";
import { SitePartSupplierForm } from "src/components/views/erp/parts/siteParts/SitePartSupplierForm.tsx";
import { SitePartSupplierEditApi } from "src/api/generated/erp/parts/sitePart/api/sitePartSupplierEditApi.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SitePartSuppliersDataGridProps {
	partId: number;
}

export const SitePartSuppliersDataGrid = ({ partId }: SitePartSuppliersDataGridProps) => {
	return (
		<ClientSideDataGridModel
			gridId={"63148594DDD342FF"}
			fetchData={() =>
				SitePartSupplierApi.getPartSuppliers({
					partId: partId,
				})
			}
			getRows={(data) => data}
			getRowId={(row) => row.supplierPartId}
			initialParams={{}}
			render={({ onlySelectedRow, dataGridProps, refreshData }) => {
				return (
					<FormCrudDataGrid<SupplierPartView>
						columns={[
							textColumn({
								field: "supplierName",
								headerName: i18n.t("supplier"),
								width: 200,
							}),
							textColumn({
								field: "purchaseUnit",
								headerName: i18n.t("purchase_unit"),
								width: 120,
							}),
							textColumn({
								field: "purchaseFactor",
								headerName: i18n.t("purchase_factor"),
								width: 120,
							}),
							textColumn({
								field: "purchasePriceUnit",
								headerName: i18n.t("purchase_price_unit"),
								width: 135,
							}),
							textColumn({
								field: "purchasePriceFactor",
								headerName: i18n.t("purchase_price_factor"),
								width: 135,
							}),
							textColumn({
								field: "purchasePrice",
								headerName: i18n.t("purchase_price"),
							}),
							textColumn({
								field: "packSize",
								headerName: i18n.t("pack_size"),
							}),
							textColumn({
								field: "supplierPartNo",
								headerName: i18n.t("supplier_part_no"),
								width: 150,
							}),
							textColumn({
								field: "supplierPartDescription",
								headerName: i18n.t("supplier_part_description"),
								width: 140,
							}),
							textColumn({
								field: "deliveryDuration",
								headerName: i18n.t("delivery_duration"),
							}),
							coloredBooleanColumn({
								field: "isDefault",
								headerName: i18n.t("default"),
								falseColor: "grey",
							}),
						]}
						actionBarComponents={
							<>
								<DocumentsOfObjectButton
									objectRef={nullableAavoObjectRef("SUPPLIER_PART", onlySelectedRow?.supplierPartId)}
								/>
							</>
						}
						rowContextMenuComponents={({ row }) => [
							<OpenObjectChangeLogButton
								key={"changeLog"}
								objectRef={{
									objectType: "SUPPLIER_PART",
									objectId: row.supplierPartId,
								}}
							/>,
							!row.isDefault && (
								<AsyncMenuButton
									label={i18n.t("set_to_default")}
									icon={faCheck}
									onClick={async () => {
										await SitePartSupplierApi.setDefaultSupplier({
											partId: partId,
											supplierId: row.supplierId,
										});
										await refreshData();
									}}
								/>
							),
							row.isDefault && (
								<AsyncMenuButton
									label={i18n.t("remove_default")}
									icon={faXmark}
									onClick={async () => {
										await SitePartSupplierApi.unsetDefaultSupplier({
											partId: partId,
											supplierId: row.supplierId,
										});
										await refreshData();
									}}
								/>
							),
						]}
						form={{
							addRowEnabled: true,
							editEnabled: true,
							dialogSize: "sm",
							dialogTitle: i18n.t("supplier_part"),
							agentContext: (row) => ({
								key: defineAgentUiContextKey("erp.parts.sitePartSupplier.formDialog"),
								title: i18n.t("supplier_part"),
								data: {
									partId,
									supplierPartId: row?.supplierPartId ?? null,
									supplierId: row?.supplierId ?? null,
								},
							}),
							component: ({ row, ...other }) => (
								<SitePartSupplierForm partId={partId} supplierPartId={row?.supplierPartId} {...other} />
							),
						}}
						remove={{
							type: "enabled",
							action: async (params) =>
								await SitePartSupplierEditApi.delete_({
									supplierParts: params.items,
								}),
						}}
						agentContext={{
							key: defineAgentUiContextKey("erp.parts.sitePartSuppliers.grid"),
							data: { partId },
							rowFields: ["supplierPartId", "partId", "supplierId", "supplierName", "supplierPartNo"],
							selectedRowPathSegment: (row) => row.supplierName,
						}}
						{...dataGridProps}
					/>
				);
			}}
		/>
	);
};
